import { Box } from '@mui/material';
import { FormDataConsumer, useRecordContext } from 'ra-core';
import React, { useState } from 'react';
import {
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
  NumberInput,
  TextInput,
} from 'react-admin';
import RequesterInputs, { APPLY_TYPES } from 'resources/relationships/RequesterInputs';
import QuickCreateButton from './QuickCreateButton';
import { choicesFromEnum } from 'utils/helpers';
import { ENUMS } from './Enum/enums';
import _ from 'lodash';

const sortByName = (arr) => _.sortBy(arr, 'name');

const choices = sortByName(
  choicesFromEnum(ENUMS.Relationship.kind, {
    strings: true,
  }),
);

type Requester = 'Location' | 'Professional';

const blockFields = ['reason_id', 'reason_type'];
const sharedFields = ['comments', 'requestee_type', 'requester_type'];

const transformMultipleValues = (values) => {
  const isBlock = values.kind === 'block';

  const fields = ['requester_ids', ...sharedFields];
  const blockParams = isBlock ? _.pick(values, blockFields) : {};

  return {
    ..._.pick(values, fields),
    ...blockParams,
    requestee_ids: [values.requestee_id],
  };
};

const transformValues = (values) =>
  _.pick(values, [
    'comments',
    'kind',
    'mass_block',
    'requestee_id',
    'requester_id',
    ...blockFields,
    ...sharedFields,
  ]);

export const MassBlockFavoriteDialog = ({
  requester,
  requestee,
}: {
  requester: Requester;
  requestee: Requester;
}) => {
  const [kind, setKind] = useState<string | undefined>(undefined);
  const [isMultiple, setIsMultiple] = useState<boolean>(false);

  const resource = isMultiple ? `mass_${kind}` : 'Relationship';
  const url = isMultiple ? `/v3/admin/${resource}` : undefined;
  const transform = isMultiple ? transformMultipleValues : transformValues;

  const isLocation = requester === 'Location';
  const reasonType = isLocation ? 'LocationBlockReason' : 'ProfessionalBlockReason';
  const record = useRecordContext();
  const requesterId = record?.id;

  const data = {
    apply_type: APPLY_TYPES.CURRENT_ONLY,
    mass_block: {
      apply_to_vms_type: false,
      apply_to_health_system: false,
      apply_to_msp: false,
    },
    requester_id: requesterId,
    requester_ids: [],
    requester_type: requester,
    requestee_type: requestee,
  };

  return (
    <Box my={3}>
      <QuickCreateButton initialValues={data} transform={transform} resource={resource} url={url}>
        <SelectInput
          source="kind"
          choices={choices}
          data-testid="professional_show-relationships_tab_create_kind"
        />
        <FormDataConsumer>
          {(formDataProps) => {
            const formData = formDataProps.formData || {};
            const isBlock = formData.kind === 'block';
            const blockReasonFilter = reasonType === 'LocationBlockReason' ? { active: true } : {};

            return (
              <>
                {isBlock && (
                  <>
                    <Box>
                      <ReferenceInput
                        reference={reasonType}
                        source="reason_id"
                        sort={{ field: 'label', order: 'ASC' }}
                        perPage={100}
                        filter={blockReasonFilter}
                      >
                        <AutocompleteInput optionText="label" />
                      </ReferenceInput>
                    </Box>
                    <Box>
                      <SelectInput
                        defaultValue={reasonType}
                        source="reason_type"
                        choices={[
                          {
                            id: reasonType,
                            name: reasonType,
                          },
                        ]}
                      />
                    </Box>
                  </>
                )}
                <RequesterInputs
                  requester={requester}
                  companyId={record?.company_id}
                  onKindChange={setKind}
                  onIsMultipleChange={setIsMultiple}
                />
              </>
            );
          }}
        </FormDataConsumer>
        <NumberInput
          source="requestee_id"
          data-testid="professional_show-relationships_tab_create_requestee_id"
        />
        <TextInput
          fullWidth
          multiline={true}
          source="comments"
          data-testid="professional_show-relationships_tab_create_comments"
        />
      </QuickCreateButton>
    </Box>
  );
};

export default MassBlockFavoriteDialog;
